/**
 * MyBookingsPage - Customer portal view of their bookings
 * Shows upcoming and past bookings with cancel/reschedule options
 */

import { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { RescheduleModal } from '../../components/modals/Reschedule';
import { CancelConfirmModal } from '../../components/modals/CancelConfirm';
import { getApiBase } from '../../lib/apiBase';

const API_BASE = getApiBase();

interface PortalBooking {
  id: string;
  scheduledAt: string;
  status: string;
  serviceType?: string;
  vehicle: {
    registration: string;
    make: string;
    model: string;
  } | null;
  canCancel: boolean;
  canReschedule: boolean;
}

const STATUS_LABELS: Record<string, string> = {
  PENDING_CONFIRMATION: 'Pendiente',
  CONFIRMED: 'Confirmada',
  CHECKED_IN: 'Recibido',
  IN_PROGRESS: 'En Proceso',
  READY_FOR_PICKUP: 'Listo',
  COMPLETED: 'Completado',
  CANCELLED: 'Cancelado',
};

export function MyBookingsPage() {
  const [bookings, setBookings] = useState<PortalBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rescheduleBooking, setRescheduleBooking] = useState<PortalBooking | null>(null);
  const [cancelBooking, setCancelBooking] = useState<PortalBooking | null>(null);

  const sessionId = localStorage.getItem('customer_session');

  const fetchBookings = async () => {
    if (!sessionId) {
      setError('Sesion no encontrada. Por favor inicie sesion nuevamente.');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/customer-portal/bookings`, {
        headers: { 'X-Customer-Session': sessionId },
      });
      if (!res.ok) throw new Error('Error al cargar citas');
      const data = await res.json();
      setBookings(data.data || []);
    } catch {
      setError('Error al cargar sus citas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBookings(); }, []);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8"><p>Cargando...</p></div>;
  }

  if (error) {
    return <div className="p-8 text-center text-red-600">{error}</div>;
  }

  return (
    <div className="max-w-2xl mx-auto p-4">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Mis Citas</h1>

      {bookings.length === 0 ? (
        <Card>
          <div className="p-8 text-center text-gray-500">
            No tiene citas programadas.
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {bookings.map((booking) => (
            <Card key={booking.id}>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                    {STATUS_LABELS[booking.status] || booking.status}
                  </span>
                  {booking.serviceType && (
                    <span className="text-sm text-gray-500">{booking.serviceType}</span>
                  )}
                </div>

                <p className="text-sm text-gray-900 font-medium">
                  📅 {formatDate(booking.scheduledAt)}
                </p>

                {booking.vehicle && (
                  <p className="text-sm text-gray-600 mt-1">
                    🚗 {booking.vehicle.registration} - {booking.vehicle.make} {booking.vehicle.model}
                  </p>
                )}

                <div className="flex gap-2 mt-3">
                  {booking.canReschedule && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRescheduleBooking(booking)}
                    >
                      Reprogramar
                    </Button>
                  )}
                  {booking.canCancel && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => setCancelBooking(booking)}
                    >
                      Cancelar
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {rescheduleBooking && (
        <RescheduleModal
          booking={rescheduleBooking}
          onClose={() => setRescheduleBooking(null)}
          onSuccess={() => { setRescheduleBooking(null); fetchBookings(); }}
        />
      )}

      {cancelBooking && (
        <CancelConfirmModal
          booking={cancelBooking}
          onClose={() => setCancelBooking(null)}
          onSuccess={() => { setCancelBooking(null); fetchBookings(); }}
        />
      )}
    </div>
  );
}
